import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface PageMeta {
  title?: string | null;
  sub?: string | null;
}

interface PageMetaContextValue {
  meta: PageMeta;
  setMeta: (next: PageMeta) => void;
  clearMeta: () => void;
}

const PageMetaContext = createContext<PageMetaContextValue | null>(null);

interface PageMetaProviderProps {
  children: React.ReactNode;
}

export function PageMetaProvider({ children }: PageMetaProviderProps) {
  const [meta, setMetaState] = useState<PageMeta>({});

  const setMeta = useCallback((next: PageMeta) => {
    setMetaState((prev) => {
      const merged = { ...prev, ...next };
      if (prev.title === merged.title && prev.sub === merged.sub) {
        return prev;
      }
      return merged;
    });
  }, []);

  const clearMeta = useCallback(() => {
    setMetaState((prev) => {
      if (prev.title === undefined && prev.sub === undefined) {
        return prev;
      }
      return {};
    });
  }, []);

  const value = useMemo<PageMetaContextValue>(
    () => ({ meta, setMeta, clearMeta }),
    [meta, setMeta, clearMeta],
  );

  return <PageMetaContext.Provider value={value}>{children}</PageMetaContext.Provider>;
}

/**
 * Read current page meta (title/sub) — used by ContextualTopBar.
 * Returns the latest values set via usePageMeta() in any descendant.
 */
export function usePageMetaValue(): PageMeta {
  const ctx = useContext(PageMetaContext);
  return ctx?.meta ?? {};
}

/**
 * Inject a dynamic page sub-text (and optionally title) for the topbar.
 * The value is cleared on unmount so the topbar falls back to the static
 * pathname-based map. Pass `undefined`/`null` to skip a field.
 *
 * @example
 *   usePageMeta({
 *     sub: `${total} produits · ${low} sous seuil · ${out} ruptures`,
 *   });
 */
export function usePageMeta(meta: PageMeta): void {
  const ctx = useContext(PageMetaContext);
  const { title, sub } = meta;
  const setMeta = ctx?.setMeta;
  const clearMeta = ctx?.clearMeta;

  useEffect(() => {
    if (!setMeta || !clearMeta) return;
    setMeta({ title: title ?? null, sub: sub ?? null });
    return () => clearMeta();
  }, [setMeta, clearMeta, title, sub]);
}
